import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Button,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tile,
  Dropdown,
  Table,
} from '@carbon/react';
import { PageHeader } from 'components/page-header';
import { useAtom } from 'jotai';
import {
  createGeologicIntervalTypeCurvesSearchAtom,
  deleteGeologicIntervalTypeCurveAtom,
} from './atoms';
import { useHistory, useLocation } from 'react-router-dom';
import { SkeletonTableRows } from 'components/skeleton-table-rows';
import { EmptyState } from 'components/empty-state';
import style from './type-curve-formations.module.scss';
import { Helmet } from 'react-helmet-async';
import { pageTitle } from 'utils/page-title';
import getFilterOptions from './utils';
import { US_DATE_FORMAT, parseMomentUtc } from 'utils/dates';
import { PaginatedTable } from 'components/table/paginated-table';
import { DestroyRecordButton } from 'components/destroy-record-button';
import { format, parse } from 'date-fns';
import { DatePicker } from 'components/date-picker';
import AddRelationshipModal from './add-relationship-modal';
import { Add } from '@carbon/react/icons';

interface GeologicIntervalTypeCurveRow {
  id: string;
  basinName: string;
  geologicInterval: string;
  typeCurveFormation: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  endedAt: string;
  positionalIndex?: number;
}

const TypeCurveFormationsPage = () => {
  const history = useHistory();
  const location = useLocation();

  const params = new URLSearchParams(location.search);

  const pageSize = parseInt(params.get('page_size') || '16', 10);

  // Create search atoms
  const searchAtoms = useMemo(
    () => createGeologicIntervalTypeCurvesSearchAtom(pageSize),
    [pageSize]
  );
  const [searchAtomValue, setSearchPayload] = useAtom(searchAtoms.searchAtom);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
  const [filterOptionsError, setFilterOptionsError] = useState<Error | null>(
    null
  );
  const [showFilters] = useState(true);
  const [selectedBasin, setSelectedBasin] = useState<string>('All');
  const [selectedGeologicInterval, setSelectedGeologicInterval] =
    useState<string>('All');
  const [asOfDateFrom, setAsOfDateFrom] = useState<Date>(new Date());
  const [asOfDateTo, setAsOfDateTo] = useState<Date>(new Date());
  const [showAddModal, setShowAddModal] = useState(false);

  const [filterOptions, setFilterOptions] = useState<{
    basins: string[];
    geologicIntervals: string[];
  }>({
    basins: [],
    geologicIntervals: [],
  });

  const deleteAtom = useMemo(() => deleteGeologicIntervalTypeCurveAtom, []);

  const mappedResults: GeologicIntervalTypeCurveRow[] = useMemo(() => {
    const results =
      searchAtomValue.data?.results.map((result) => ({
        id: result.id.toString(),
        basinName: result.basinName,
        geologicInterval: result.geologicInterval,
        typeCurveFormation: result.typeCurveFormation,
        createdBy: result.createdBy ?? '',
        createdAt: result.createdAt ?? '',
        updatedAt: result.updatedAt ?? '',
        endedAt: result.endedAt ?? '',
        positionalIndex: result.positionalIndex,
      })) || [];
    return results;
  }, [searchAtomValue.data]);

  // Handle pagination
  const handlePageChange = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    const params = new URLSearchParams(location.search);
    params.set('page', page.toString());
    params.set('page_size', pageSize.toString());
    history.push({ search: params.toString() });

    setSearchPayload((current) => ({
      ...current,
      page,
      pageSize,
    }));
  };

  useEffect(() => {
    setFilterOptionsLoading(true);
    const params = new URLSearchParams(window.location.search);
    const basinName = params.get('basin_name');
    const geologicInterval = params.get('geologic_interval');
    const asOfDateFromParam = params.get('as_of_date_from');
    const asOfDateToParam = params.get('as_of_date_to');
    const todayStr = format(new Date(), 'yyyy-MM-dd');

    // If either date is missing, set both to today in URL and payload
    if (!asOfDateFromParam || !asOfDateToParam) {
      params.set('as_of_date_from', todayStr);
      params.set('as_of_date_to', todayStr);
      history.replace({ search: params.toString() });

      setAsOfDateFrom(new Date());
      setAsOfDateTo(new Date());
      setSearchPayload((current) => ({
        ...current,
        as_of_date: { from: todayStr, to: todayStr },
      }));
    } else {
      // If present, parse and set state
      setAsOfDateFrom(parse(asOfDateFromParam, 'yyyy-MM-dd', new Date()));
      setAsOfDateTo(parse(asOfDateToParam, 'yyyy-MM-dd', new Date()));
    }

    getFilterOptions()
      .then((data) => {
        const withAllOption = {
          basins: ['All', ...data.basins],
          geologicIntervals: ['All', ...data.geologicIntervals],
        };
        setFilterOptions(withAllOption);

        if (basinName && withAllOption.basins.includes(basinName)) {
          handleFilterSelection('basin_name', basinName, setSelectedBasin);
        }
        if (
          geologicInterval &&
          withAllOption.geologicIntervals.includes(geologicInterval)
        ) {
          handleFilterSelection(
            'geologic_interval',
            geologicInterval,
            setSelectedGeologicInterval
          );
        }
      })
      .catch((error) => {
        console.error('Error fetching filter options', error);
        setFilterOptionsError(error);
      })
      .finally(() => {
        setFilterOptionsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTodayClick = useCallback(() => {
    const today = new Date();
    setAsOfDateFrom(today);
    setAsOfDateTo(today);

    const params = new URLSearchParams(location.search);
    params.set('as_of_date_from', format(today, 'yyyy-MM-dd'));
    params.set('as_of_date_to', format(today, 'yyyy-MM-dd'));
    params.set('page', '1');
    history.push({ search: params.toString() });

    setSearchPayload((current) => ({
      ...current,
      as_of_date: {
        from: format(today, 'yyyy-MM-dd'),
        to: format(today, 'yyyy-MM-dd'),
      },
      page: 1,
    }));
  }, [location.search, history, setSearchPayload]);

  // Handle filter changes
  const handleFilterChange = useCallback(
    (filterName: string, value: string) => {
      const params = new URLSearchParams(location.search);
      if (value && value !== 'All') {
        params.set(filterName, value);
      } else {
        params.delete(filterName);
      }
      params.set('page', '1');
      history.push({ search: params.toString() });

      setSearchPayload((current) => ({
        ...current,
        [filterName]: value && value !== 'All' ? value : undefined,
        page: 1,
      }));
    },
    [location.search, history, setSearchPayload]
  );

  const handleFilterSelection = useCallback(
    (
      filterName: string,
      selectedItem: string | null | undefined,
      setState: (value: string) => void
    ) => {
      if (selectedItem) {
        setState(selectedItem);
        handleFilterChange(filterName, selectedItem);
      } else {
        setState('All');
        handleFilterChange(filterName, 'All');
      }
    },
    [handleFilterChange]
  );

  return (
    <>
      <Helmet>
        <title>{pageTitle('Type Curve Formations')}</title>
      </Helmet>
      <div className={style.pageHeader}>
        <PageHeader
          title="Type Curve Formations"
          breadcrumbs={[['type_curve_formations', 'Type Curve Formations']]}
          actions={[
            <Button
              key="add-relationship"
              kind="primary"
              onClick={() => setShowAddModal(true)}
              renderIcon={Add}
            >
              Add Relationship
            </Button>,
          ]}
        />
      </div>
      <div className={style.mainContent}>
        <div className={style.filtersArea}>
          {filterOptionsError && (
            <div className={style.errorMessage}>
              <p>
                Error fetching filter options. Please reach out on
                #insights-oncall if this issue persists.
              </p>
            </div>
          )}
          {showFilters && !filterOptionsLoading && (
            <div className={style.filtersRow}>
              <div className={style.filterContainer}>
                <h4>As of Date</h4>
                <div className={style.dateFilterWrapper}>
                  <div className={style.datePickerWrapper}>
                    <DatePicker
                      id="asOfDateFrom"
                      data-testid="as-of-date-from"
                      onChange={(dates: Date[]) => {
                        const fromDate = dates[0];
                        setAsOfDateFrom(fromDate);

                        const params = new URLSearchParams(location.search);
                        params.set(
                          'as_of_date_from',
                          format(fromDate, 'yyyy-MM-dd')
                        );
                        // Keep the current to-date if present
                        if (params.get('as_of_date_to')) {
                          params.set(
                            'as_of_date_to',
                            params.get('as_of_date_to')!
                          );
                        } else {
                          params.set(
                            'as_of_date_to',
                            format(fromDate, 'yyyy-MM-dd')
                          );
                        }
                        params.set('page', '1');
                        history.push({ search: params.toString() });

                        setSearchPayload((current) => ({
                          ...current,
                          as_of_date: {
                            from: format(fromDate, 'yyyy-MM-dd'),
                            to:
                              current?.as_of_date?.to ||
                              format(fromDate, 'yyyy-MM-dd'),
                          },
                          page: 1,
                        }));
                      }}
                      value={format(asOfDateFrom, 'yyyy-MM-dd')}
                      maxDate={format(new Date(), 'MM/dd/yyyy')}
                      labelText=""
                    />
                  </div>
                  <div className={style.datePickerWrapper}>
                    <DatePicker
                      id="asOfDateTo"
                      data-testid="as-of-date-to"
                      onChange={(dates: Date[]) => {
                        const toDate = dates[0];
                        setAsOfDateTo(toDate);

                        const params = new URLSearchParams(location.search);
                        params.set(
                          'as_of_date_to',
                          format(toDate, 'yyyy-MM-dd')
                        );
                        // Keep the current from-date if present
                        if (params.get('as_of_date_from')) {
                          params.set(
                            'as_of_date_from',
                            params.get('as_of_date_from')!
                          );
                        } else {
                          params.set(
                            'as_of_date_from',
                            format(toDate, 'yyyy-MM-dd')
                          );
                        }
                        params.set('page', '1');
                        history.push({ search: params.toString() });

                        setSearchPayload((current) => ({
                          ...current,
                          as_of_date: {
                            from:
                              current?.as_of_date?.from ||
                              format(toDate, 'yyyy-MM-dd'),
                            to: format(toDate, 'yyyy-MM-dd'),
                          },
                          page: 1,
                        }));
                      }}
                      value={format(asOfDateTo, 'yyyy-MM-dd')}
                      maxDate={format(new Date(), 'MM/dd/yyyy')}
                      labelText=""
                    />
                    <Button
                      kind="ghost"
                      size="sm"
                      onClick={handleTodayClick}
                      className={style.todayButton}
                      data-testid="today-button"
                    >
                      Today
                    </Button>
                  </div>
                </div>
              </div>
              <div className={style.filterContainer}>
                <h4>Basin</h4>
                <Dropdown
                  id="basin-filter"
                  data-testid="basin-filter"
                  titleText=""
                  label="Select"
                  items={filterOptions.basins}
                  itemToString={(item) => (item ? item : '')}
                  onChange={({ selectedItem }) =>
                    handleFilterSelection(
                      'basin_name',
                      selectedItem,
                      setSelectedBasin
                    )
                  }
                  selectedItem={selectedBasin}
                />
              </div>

              <div className={style.filterContainer}>
                <h4>Geologic Interval</h4>
                <Dropdown
                  id="geologic-interval-filter"
                  data-testid="geologic-interval-filter"
                  titleText=""
                  label="Select"
                  items={filterOptions.geologicIntervals}
                  itemToString={(item) => (item ? item : '')}
                  onChange={({ selectedItem }) =>
                    handleFilterSelection(
                      'geologic_interval',
                      selectedItem,
                      setSelectedGeologicInterval
                    )
                  }
                  selectedItem={selectedGeologicInterval}
                />
              </div>
            </div>
          )}
        </div>

        <div>
          <PaginatedTable
            pageSizes={[16, 24, 48, 64]}
            pageSize={pageSize}
            size="lg"
            className={style.table}
            page={searchAtomValue.currentPayload?.page || 1}
            totalItems={searchAtomValue?.data?.totalCount || 0}
            persistentPagination
            isLoading={searchAtomValue.loading || filterOptionsLoading}
            loadingComponent={
              <Table size="lg">
                <TableHead>
                  <TableRow>
                    <TableHeader>Basin</TableHeader>
                    <TableHeader>Geologic Interval</TableHeader>
                    <TableHeader>Type Curve Formation</TableHeader>
                    <TableHeader>Created By</TableHeader>
                    <TableHeader>Created At</TableHeader>
                    <TableHeader>Updated At</TableHeader>
                    <TableHeader></TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <SkeletonTableRows rows={5} columns={7} />
                </TableBody>
              </Table>
            }
            emptyStateComponent={
              <Tile>
                <EmptyState
                  headerText="No Type Curve Formations Available"
                  helperText="Currently there are no type curve formations found"
                />
              </Tile>
            }
            onPaginate={handlePageChange}
          >
            <TableHead>
              <TableRow>
                <TableHeader>Basin</TableHeader>
                <TableHeader>Geologic Interval</TableHeader>
                <TableHeader>Type Curve Formation</TableHeader>
                <TableHeader>Created By</TableHeader>
                <TableHeader>Created At</TableHeader>
                <TableHeader>Updated At</TableHeader>
                <TableHeader></TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {mappedResults.map((row, index) => (
                <TableRow key={`${row.id}-${index}`}>
                  <TableCell>{row.basinName}</TableCell>
                  <TableCell>{row.geologicInterval}</TableCell>
                  <TableCell>{row.typeCurveFormation}</TableCell>
                  <TableCell>{row.createdBy || '-'}</TableCell>
                  <TableCell>
                    {row.createdAt
                      ? parseMomentUtc(row.createdAt).format(US_DATE_FORMAT)
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {row.updatedAt
                      ? parseMomentUtc(row.updatedAt).format(US_DATE_FORMAT)
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <div className={style.actionButtons}>
                      {!row.endedAt && (
                        <DestroyRecordButton
                          deleteAtom={deleteAtom}
                          url={`/type_curve_formations/${row.id}`}
                          onSuccess={() => {
                            setSearchPayload((current) => ({
                              ...current,
                              page: 1,
                            }));
                          }}
                          modalHeading="Remove Relationship"
                          hasIconOnly={true}
                        >
                          <div
                            className={style.confirmationMessage}
                            data-testid="confirmation-message"
                          >
                            <p>
                              Are you sure you want to remove{' '}
                              <strong>{row.geologicInterval}</strong> to{' '}
                              <strong>{row.typeCurveFormation}</strong>{' '}
                              relationship? This action cannot be undone and
                              will affect all associated data.
                            </p>
                          </div>
                        </DestroyRecordButton>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </PaginatedTable>
        </div>
      </div>
      {showAddModal && (
        <AddRelationshipModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSaved={() => {
            setShowAddModal(false);
            setSearchPayload((current) => ({
              ...current,
              page: 1,
            }));
          }}
        />
      )}
    </>
  );
};

export default TypeCurveFormationsPage;
